/*
 * A copy of the dispatch console's module of the same name, kept byte-for-byte except for the
 * storage keys and the prose that named the other console.
 *
 * Nothing in it is design content: it is the OpenID Connect authorization-code flow with PKCE, the
 * token refresh, the expiry and the claim reading, all of which both consoles need identically.
 * It is a copy rather than a shared package because this repository builds every package in
 * `packages/` through three explicit steps in the root `package.json`, and a sixth package that
 * `npm run check` could not build before its consumers would be worse than this duplication.
 * `docs/adr/0021-two-consoles-and-no-shared-shell.md` records the decision, what it costs, and the
 * precondition for undoing it.
 */
/**
 * Where the console gets its identity provider from, and what it refuses to guess.
 *
 * Six variables, read once at start-up. Three are required and three have defaults, and the split is
 * the whole content of this file: a value that cannot be guessed safely has no default, so a build
 * missing it says so on the screen rather than redirecting an operator to a provider that does not
 * exist.
 *
 * `VITE_` prefixed, because that is the only prefix Vite exposes to browser code — an unprefixed
 * variable is simply absent at runtime, which would be a configuration mistake that looks like a
 * code one.
 */

export interface OidcConfiguration {
  /**
   * The realm's issuer, absolute.
   *
   * Accepts an origin-relative path as well, resolved against the page's own origin, because that is
   * how this is deployed: Keycloak answers at a path on the console's own origin, which makes its
   * session cookie first-party and means no third-party cookie policy can decide whether sign-in
   * works. `/auth/realms/tdelivr` and `https://ops.example.com/auth/realms/tdelivr` are the same
   * thing written two ways, and the relative form is the one that survives being deployed twice.
   */
  readonly issuer: string;
  readonly clientId: string;
  /**
   * The audience the services verify, or undefined when the deployment has not stated one.
   *
   * Used twice. It is sent as the `audience` parameter, which providers that implement resource
   * indicators honour and others ignore. And it is checked on the token that comes back — because a
   * token with the wrong `aud` is refused by every one of the 142 services, so an operator would
   * sign in successfully and then find every screen in its error state with nothing saying why. A
   * sign-in that fails naming the audience is strictly more useful than a console that works for
   * nothing.
   *
   * Optional rather than required so that a deployment which has not configured an audience mapper
   * yet gets a console it can still sign into. Configured means checked; absent means not checked.
   */
  readonly audience?: string;
  /**
   * What to ask for, space-delimited.
   *
   * `openid` alone by default, and that is deliberate rather than minimal-for-its-own-sake. Keycloak
   * refuses an entire authorization request that names a scope the client does not have, so a
   * console that listed the platform scopes it wanted would stop working the moment a scope was
   * renamed — and the platform's scopes are supposed to arrive as the client's *default* scopes,
   * which are included without being asked for. ADR 0014 settles that: scopes are Keycloak client
   * scopes, derived from the `requireScope` call sites.
   */
  readonly scopes: string;
  /** Absolute, and must match a redirect URI registered on the client. */
  readonly redirectUri: string;
  /**
   * What the sign-in screen calls the provider.
   *
   * The design names the organisation's provider in three places — the waiting state, the failure
   * state and the button that leaves for it — and nothing in the platform publishes that name to an
   * unauthenticated browser. See Q-16. The default is a true sentence fragment rather than a
   * placeholder, so an unconfigured deployment reads "Waiting for your organisation's sign-in."
   */
  readonly providerName: string;
}

/**
 * What a build is missing, when it is missing something.
 *
 * Named variables rather than a boolean, because the person who sees this screen is the person who
 * can fix it and the fix is one of six specific strings.
 */
export interface ConfigurationProblem {
  readonly missing: readonly string[];
}

/** The subset of `import.meta.env` this reads. Declared in `src/vite-env.d.ts`. */
export interface OidcEnvironment {
  readonly VITE_OIDC_ISSUER?: string | undefined;
  readonly VITE_OIDC_CLIENT_ID?: string | undefined;
  readonly VITE_OIDC_AUDIENCE?: string | undefined;
  readonly VITE_OIDC_SCOPES?: string | undefined;
  readonly VITE_OIDC_REDIRECT_PATH?: string | undefined;
  readonly VITE_OIDC_PROVIDER_NAME?: string | undefined;
}

function trimmed(value: string | undefined): string | undefined {
  const text = value?.trim();
  return text === undefined || text.length === 0 ? undefined : text;
}

/**
 * Resolve a value that may be a whole URL or a path on this origin.
 *
 * `new URL(value, origin)` does both: an absolute URL ignores the base, a path resolves against it.
 * The same two lines decide the issuer and the redirect URI, and getting it wrong in either place
 * produces a redirect loop rather than an error, which is why it is one function.
 */
function resolved(value: string, origin: string): string {
  return new URL(value, origin.endsWith("/") ? origin : `${origin}/`).toString();
}

export function readOidcConfiguration(
  environment: OidcEnvironment,
  origin: string,
): OidcConfiguration | ConfigurationProblem {
  const issuer = trimmed(environment.VITE_OIDC_ISSUER);
  const clientId = trimmed(environment.VITE_OIDC_CLIENT_ID);

  const missing: string[] = [];
  if (issuer === undefined) missing.push("VITE_OIDC_ISSUER");
  if (clientId === undefined) missing.push("VITE_OIDC_CLIENT_ID");
  if (issuer === undefined || clientId === undefined) return { missing };

  const audience = trimmed(environment.VITE_OIDC_AUDIENCE);
  return {
    // Trailing slash removed, because this string is compared with the `issuer` the discovery
    // document states and one of the two spellings would otherwise fail a comparison that is
    // checking something else.
    issuer: resolved(issuer, origin).replace(/\/$/, ""),
    clientId,
    ...(audience === undefined ? {} : { audience }),
    scopes: trimmed(environment.VITE_OIDC_SCOPES) ?? "openid",
    // The default is the origin itself. A console served from a sub-path configures the path it is
    // served from, because a redirect URI that is not the page the operator lands on is a redirect
    // to somebody else's document. Left exactly as resolved: a redirect URI is compared literally
    // by the provider, so adding or removing a slash here is a rejected request.
    redirectUri: resolved(trimmed(environment.VITE_OIDC_REDIRECT_PATH) ?? "/", origin),
    providerName: trimmed(environment.VITE_OIDC_PROVIDER_NAME) ?? "your organisation's sign-in",
  };
}

export function isConfigured(
  value: OidcConfiguration | ConfigurationProblem,
): value is OidcConfiguration {
  return !("missing" in value);
}
